/**
 * The ★ map rules overlay and the team play rules as plain data (design v3,
 * Variant 1: a "pause screen" scrim over the live board with the rule
 * territories spotlit, plus a legend). Pure: no React. `components/map/rules/`
 * draws it. Team play rules have no board marks: they show in a modal from
 * the game menu (components/game/TeamRulesPanel).
 *
 * Both produce the same {@link OverlayModel}: legend rows, footer
 * chips and board marks. Every mark belongs to a row. With no row isolated the
 * board shows each row's overview marks; isolating a row shows only that row,
 * detail marks included. "Calm" overview: a rule with many arrows (links,
 * reach, matchups) shows ONE example arrow until its row is isolated, and a
 * row's secondary sets (`also`) are detail only.
 *
 * Board positions are map (viewBox) units. Stroke widths, radii and offsets
 * are screen pixels; the renderer converts them at the current zoom.
 */
import {
  hasBorderTo,
  isAlly,
  resolveRuleSets,
  ruleLinkPairs,
  ruleMatchupPairs,
  ruleReachTargets,
  teamLabel,
  teamOf,
  teamMembers,
  type MapDef,
  type PlayerId,
  type RuleFact,
  type RuleGlyph,
  type RuleOverlay,
  type TeamId,
  type TeamMembership,
} from '@warcarp/api-types'
import type { OverlayPalette } from './rulesOverlayTheme'
import { diceEdge, edgeVerdict, oddsSentence, type DiceEdge } from './diceFacts'

export interface Pt { x: number; y: number }

/** Glyphs a chip can carry: a set's glyph or one of the fact icons. */
export type ChipGlyph = RuleGlyph | 'sword' | 'shield' | 'link' | 'reach' | 'oneway' | 'cards' | 'cap' | 'none' | 'clock'

export type ChipPart =
  /** Stencil text: "+8", "max 7". */
  | { t: 'text'; text: string }
  /** Slab label: "each", "whole area". */
  | { t: 'label'; text: string; soft?: boolean }
  | { t: 'glyph'; glyph: ChipGlyph; color: string }
  /** A MaterialCommunityIcons glyph (team chips). */
  | { t: 'icon'; name: string; color: string }
  /** A small stencil role word: "ATK", "DEF", "REACH", "ONE-WAY". */
  | { t: 'key'; text: string }
  /** A larger stencil value after a key: "d5", "2". */
  | { t: 'num'; text: string }
  /** A thin divider in the chip's colour (between ATK and DEF, or reach and one-way). */
  | { t: 'sep' }

export interface ChipSpec {
  color: string
  parts: ChipPart[]
  bonus?: boolean
  /** A dice chip: tapping it expands this sentence (with the odds) under the row. */
  expand?: string
  /** The chip in words, for screen readers (dice chips). */
  accessibilityLabel?: string
  /** A plain line rather than a pill: an icon and a short sentence, full width (team play). */
  line?: boolean
}

export type RowTile =
  | { kind: 'glyph'; glyph: RuleGlyph; color: string }
  | { kind: 'icon'; name: string; color: string }
  | { kind: 'team'; team: TeamId; color: string; initial: string }

export interface OverlayRow {
  id: string
  label: string
  /** The tile's border. */
  color: string
  tile: RowTile
  chips: ChipSpec[]
  /** Spoken in place of the chips. */
  accessibilityLabel: string
}

export interface FooterChip {
  /** A rule glyph (map chips) or a MaterialCommunityIcons name (team chips). */
  glyph?: ChipGlyph
  icon?: string
  label: string
  color: string
}

interface MarkBase {
  row: string
  /** Shown only while the row is isolated. */
  detail: boolean
}

export type OverlayMark = MarkBase & (
  /** Territories / regions cut out of the scrim and outlined. */
  | {
    kind: 'lit'
    territoryIds: string[]
    regionIds: string[]
    color: string
    fillOpacity: number
    /** Outline width in px. */
    strokePx: number
    dashed?: boolean
    /** A soft halo under the outline. */
    glow?: boolean
    /** Cut out of the scrim too (default true). */
    hole?: boolean
    /** Territories this mark never paints into (a mesa's outline stops at the towers and crystals on it). */
    clipOut?: string[]
  }
  /** Cut out of the scrim, not outlined. */
  | { kind: 'hole'; territoryIds: string[] }
  | {
    kind: 'arrow'
    from: Pt
    to: Pt
    color: string
    widthPx: number
    /** Curve, as a fraction of the arrow's length (+ bends left of travel). */
    bulge: number
    head: boolean
    /** Gap at each end, px. */
    startGapPx: number
    endGapPx: number
    dash?: string
  }
  /** A pill. `at` is a point or the middle of an arrow (`arrowOf`, the arrow mark's index). */
  | {
    kind: 'tag'; at: Pt | { arrowOf: number }; dyPx: number; dxPx?: number; text: string; color: string; icon?: string
    /** A dice matchup tag: drawn as a bold "5", a small light "v" and a bold "7". */
    dice?: { attack: number; defend: number }
    /**
     * Who rolls which: the attacking and defending sets' glyphs, drawn before
     * each number (a plains swatch before the 5, a tower cross before the 7).
     */
    roles?: { attack: { glyph: ChipGlyph; color: string }; defend: { glyph: ChipGlyph; color: string } }
    /** A rule glyph drawn before the text (the reach tag's dashed arrow). */
    glyph?: { glyph: ChipGlyph; color: string }
    /**
     * Placed by the board layer: right of `at` if that spot is clear, else the
     * nearest spot round it that covers nothing lit in the overview.
     */
    avoidLit?: boolean
    /** The tag in words, for screen readers. */
    label?: string
  }
  /** A pulsing ring round a territory's centre. */
  | { kind: 'ring'; at: Pt; color: string; radiusPx: number }
)

export interface OverlayModel {
  kind: 'map' | 'team'
  title: string
  /** A second title line (team play: who is with and against you). The map overlay has none. */
  subtitle?: string
  rows: OverlayRow[]
  footer: FooterChip[]
  /** Show the "how dice work" key under the legend (the map has a dice fact). */
  diceKey?: boolean
  marks: OverlayMark[]
  /** Read by screen readers instead of the board marks. */
  accessibilitySummary: string
  /** Scrim strength, 0–1 of the palette's (the players filter dims about half as much). */
  scrimStrength?: number
}

/** Is the mark on screen? Isolated: that row's marks only, detail included. */
export function markVisible(mark: { row: string; detail: boolean }, isolatedRow: string | null): boolean {
  return isolatedRow ? mark.row === isolatedRow : !mark.detail
}

/** Where a pulse ring, place tag or arrow attaches: the territory's centre. */
function centreOf(map: Pick<MapDef, 'territories'>, id: string): Pt | null {
  const t = map.territories.find((x) => x.id === id)
  return t ? { x: t.position.x, y: t.position.y } : null
}

type Fact = RuleFact
type SetLabel = (id: string | undefined) => string

const lower = (s: string) => s.toLowerCase()

/**
 * A rule set's name with an indefinite article: "a tower", "an islet". A name
 * that already carries a determiner ("The Core", "the sand") or reads as a
 * proper name (capitalised past its first word, "Ozmoz Hub") takes none.
 */
export function withArticle(name: string): string {
  if (/^(the|a|an|each|every|any|your|their)\s/i.test(name)) return name
  if (isProperName(name)) return name
  return /^[aeiou]/i.test(name) ? `an ${name}` : `a ${name}`
}

/** Capitalised past its first word and not led by "The": "Ozmoz Hub". */
function isProperName(name: string): boolean {
  return !/^the\s/i.test(name) && /\s[A-Z]/.test(name.trim())
}

/** "the tower", but never "the the core". */
export function withThe(name: string): string {
  return /^the\s/i.test(name) ? name : `the ${name}`
}
const plural = (s: string) => (s.endsWith('s') ? s : `${s}s`)

/** The legend's colour for a dice fact: green helps the attacker, red the defender. */
function diceColor(edge: DiceEdge, p: OverlayPalette) {
  return edge === 'attacker' ? p.good : edge === 'defender' ? p.bad : p.ink
}

/**
 * A dice chip, option B: "🛡 DEF d4", "⚔ ATK d9", "⚔ ATK d5 │ 🛡 DEF d7", led
 * by the attacking set's glyph for a matchup. Shared with the attack popup.
 */
export function diceChip(
  { attack, defend, from, vs }: { attack?: number; defend?: number; from?: { glyph: RuleGlyph; color: string }; vs?: string },
  p: OverlayPalette,
  words?: { sentence: string },
): ChipSpec {
  const c = diceColor(diceEdge(attack, defend), p)
  const atk: ChipPart[] = attack ? [{ t: 'glyph', glyph: 'sword', color: c }, { t: 'key', text: 'ATK' }, { t: 'num', text: `d${attack}` }] : []
  const def: ChipPart[] = defend ? [{ t: 'glyph', glyph: 'shield', color: c }, { t: 'key', text: 'DEF' }, { t: 'num', text: `d${defend}` }] : []
  const parts: ChipPart[] = [
    ...(from ? [{ t: 'glyph' as const, glyph: from.glyph, color: from.color }] : []),
    ...atk,
    ...(atk.length && def.length ? [{ t: 'sep' as const }] : []),
    ...def,
    ...(vs ? [{ t: 'label' as const, text: `vs ${vs}`, soft: true }] : []),
  ]
  const chip: ChipSpec = { color: c, parts }
  if (words) {
    chip.accessibilityLabel = words.sentence
    chip.expand = `${words.sentence} ${oddsSentence(attack, defend)}`
  }
  return chip
}

/**
 * One fact as a full sentence (design X5): for screen readers, and, for dice
 * facts, the line a tapped chip expands into (with the odds added).
 */
export function factSentence(f: Fact, label: SetLabel, ruleSet: string): string {
  // Short and concrete (maintainer, 2026-11-03): who attacks what, who rolls
  // which dice, and a verdict. Normal dice are 6-sided on both sides.
  // Lower-cased mid-sentence, except a proper name ("Ozmoz Hub").
  const a = (s: string) => (isProperName(s) ? withArticle(s) : lower(withArticle(s)))
  switch (f.kind) {
    case 'bonus': return `+${f.value} bonus${f.label ? ` (${f.label})` : ''}.`
    case 'defend':
      return `Attacking ${a(ruleSet)}${f.vs ? ` from ${lower(withThe(label(f.vs)))}` : ''}: you roll 6-sided dice, it rolls ${f.die}-sided. ${edgeVerdict(diceEdge(undefined, f.die))}`
    case 'attack':
      return `Attacking from ${a(ruleSet)}: you roll ${f.die}-sided dice, the defender rolls 6-sided. ${f.die > 6 ? 'Stronger attacks.' : 'Weaker attacks.'}`
    case 'matchup':
      return `${plural(label(f.from))} attacking ${a(label(f.to))}: you roll ${f.attack}-sided dice, it rolls ${f.defend}-sided. ${edgeVerdict(diceEdge(f.attack, f.defend))}`
    case 'reach': {
      const where = f.steps === 'area' ? 'anywhere in its area' : `${f.to ? lower(plural(label(f.to))) : 'territories'} up to ${f.steps} spaces away`
      return `Attacks ${where}.${f.oneWay ? ' Those spaces cannot attack or move back.' : ''}${f.attack ? ` ${factSentence({ kind: 'attack', die: f.attack }, label, ruleSet)}` : ''}`
    }
    case 'link': {
      const head = f.style === 'oneway'
        ? `One-way into the ${lower(label(f.to))}${f.label ? ` (${f.label})` : ''}: you can attack and move in, but not back out.`
        : f.style === 'warp' ? `${plural(ruleSet)} border each other.`
        : f.style === 'sea' ? `${plural(ruleSet)} link across the sea${f.label ? `, ${f.label}` : ''}.`
        : `Linked${f.label ? ` ${f.label}` : ''}.`
      return f.dice ? `${head} Attacking one from another: you roll ${f.dice.attack}-sided dice, it rolls ${f.dice.defend}-sided. ${edgeVerdict(diceEdge(f.dice.attack, f.dice.defend))}` : head
    }
    case 'cap': return `Holds at most ${f.max} troops.`
    case 'noBonus': return 'No bonus.'
  }
}

/** Legend chips for one rule's facts (design option B dice chips, R2 reach chip). */
export function ruleChips(rule: RuleOverlay['rules'][number], overlay: RuleOverlay, p: OverlayPalette): ChipSpec[] {
  const sets = new Map(overlay.sets.map((s) => [s.id, s]))
  const label: SetLabel = (id) => (id ? sets.get(id)?.label ?? id : '')
  const ruleSet = rule.label ?? label(rule.set)
  const out: ChipSpec[] = []
  for (const f of rule.facts) {
    switch (f.kind) {
      case 'bonus':
        out.push({ color: p.brass, bonus: true, parts: [{ t: 'text', text: `+${f.value}` }, ...(f.label ? [{ t: 'label' as const, text: f.label, soft: true }] : [])] })
        break
      case 'defend':
        out.push(diceChip({ defend: f.die, vs: f.vs ? label(f.vs) : undefined }, p, { sentence: factSentence(f, label, ruleSet) }))
        break
      case 'attack':
        out.push(diceChip({ attack: f.die }, p, { sentence: factSentence(f, label, ruleSet) }))
        break
      case 'matchup': {
        const from = sets.get(f.from)
        out.push(diceChip({ attack: f.attack, defend: f.defend, from: from ? { glyph: from.glyph, color: from.color } : undefined }, p, { sentence: factSentence(f, label, ruleSet) }))
        break
      }
      case 'reach': {
        // R2: one chip, "REACH 2 [plains] │ ONE-WAY".
        const to = f.to ? sets.get(f.to) : null
        const parts: ChipPart[] = [{ t: 'glyph', glyph: 'reach', color: p.ink }, { t: 'key', text: 'REACH' }, { t: 'num', text: f.steps === 'area' ? 'area' : String(f.steps) }]
        if (to) parts.push({ t: 'glyph', glyph: to.glyph, color: to.color })
        if (f.oneWay) parts.push({ t: 'sep' }, { t: 'key', text: 'ONE-WAY' })
        out.push({ color: p.ink, parts, accessibilityLabel: factSentence({ ...f, attack: undefined }, label, ruleSet) })
        if (f.attack) out.push(diceChip({ attack: f.attack }, p, { sentence: factSentence({ kind: 'attack', die: f.attack }, label, ruleSet) }))
        break
      }
      case 'link': {
        const to = f.to ? sets.get(f.to) : null
        if (f.style === 'oneway') {
          out.push({ color: p.ink, parts: [{ t: 'glyph', glyph: 'oneway', color: p.ink }, ...(to ? [{ t: 'glyph' as const, glyph: to.glyph, color: to.color }] : []), { t: 'key', text: 'ONE-WAY' }, ...(f.label ? [{ t: 'label' as const, text: f.label }] : [])] })
        } else {
          out.push({ color: p.ink, parts: [{ t: 'glyph', glyph: 'link', color: p.ink }, { t: 'label', text: f.style === 'warp' ? 'all linked' : f.label ?? 'linked' }] })
        }
        if (f.dice) out.push(diceChip({ attack: f.dice.attack, defend: f.dice.defend }, p, { sentence: factSentence(f, label, ruleSet) }))
        break
      }
      case 'cap':
        out.push({ color: p.inkSoft, parts: [{ t: 'glyph', glyph: 'cap', color: p.inkSoft }, { t: 'text', text: `max ${f.max}` }] })
        break
      case 'noBonus':
        out.push({ color: p.inkSoft, parts: [{ t: 'glyph', glyph: 'none', color: p.inkSoft }, { t: 'label', text: 'no bonus' }] })
        break
    }
  }
  return out
}

/** Does the rule have a dice fact (the legend then shows the "how dice work" key)? */
const hasDice = (f: Fact) => f.kind === 'defend' || f.kind === 'attack' || f.kind === 'matchup' || (f.kind === 'reach' && !!f.attack) || (f.kind === 'link' && !!f.dice)

// ─── Example placement ──────────────────────────────────────────────────────

/** Points along an arrow's curve (the renderer's quadratic, ends included), map units. */
export function arrowPoints(from: Pt, to: Pt, bulge: number, n = 12): Pt[] {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const len = Math.hypot(dx, dy) || 1
  const c = { x: (from.x + to.x) / 2 - (dy / len) * len * bulge, y: (from.y + to.y) / 2 + (dx / len) * len * bulge }
  const out: Pt[] = []
  for (let i = 0; i <= n; i++) {
    const t = i / n
    const u = 1 - t
    out.push({ x: u * u * from.x + 2 * u * t * c.x + t * t * to.x, y: u * u * from.y + 2 * u * t * c.y + t * t * to.y })
  }
  return out
}

/** The middle of an arrow's curve: where its tag sits. */
const arrowMid = (from: Pt, to: Pt, bulge: number) => arrowPoints(from, to, bulge, 2)[1]!

/** The closest two points of two footprints (Infinity when either is empty). */
export function footprintGap(a: readonly Pt[], b: readonly Pt[]): number {
  let best = Infinity
  for (const p of a) for (const q of b) best = Math.min(best, Math.hypot(p.x - q.x, p.y - q.y))
  return best
}

/** How far apart two rules' examples must be on a map, in map units. */
export const exampleSeparation = (map: Pick<MapDef, 'viewBox'>) => Math.max(map.viewBox.width, map.viewBox.height) * 0.07

/** A rule fact's overview example and the spots it could take (authored spot first). */
interface ExampleSlot { row: string; fact: number; feet: Pt[][] }

/**
 * Choose one spot per example so that examples sit apart. Scored, in order:
 * the closest gap between any two examples (or an example and the fixed
 * marks), capped at `minSep`; then which authored spots are kept (earlier
 * examples first); then the
 * closest gap uncapped. Every combination is tried when there are few
 * (always, on the bundled maps); otherwise each example in turn takes the
 * spot farthest from those already placed. Ties go to the earlier candidate,
 * so the result is deterministic.
 */
function solveExamples(slots: readonly ExampleSlot[], fixed: readonly Pt[], minSep: number): number[] {
  const toFixed = slots.map((s) => s.feet.map((f) => footprintGap(f, fixed)))
  const combos = slots.reduce((n, s) => n * Math.max(1, s.feet.length), 1)
  if (combos > 50_000) {
    const occupied = [...fixed]
    return slots.map((s) => {
      let best = 0
      let bestGap = -1
      s.feet.forEach((f, i) => {
        const gap = footprintGap(f, occupied)
        if (i === 0 && gap >= minSep) { best = 0; bestGap = Infinity }
        if (gap > bestGap) { best = i; bestGap = gap }
      })
      occupied.push(...(s.feet[best] ?? []))
      return best
    })
  }
  const pairGap = new Map<string, number>()
  const gapOf = (i: number, a: number, j: number, b: number) => {
    const k = `${i}.${a}|${j}.${b}`
    let g = pairGap.get(k)
    if (g === undefined) { g = footprintGap(slots[i]!.feet[a]!, slots[j]!.feet[b]!); pairGap.set(k, g) }
    return g
  }
  let best: number[] = slots.map(() => 0)
  let bestScore: [number, number, number] = [-1, -1, -1]
  const pick: number[] = []
  const walk = (i: number, minGap: number, authored: number) => {
    if (i === slots.length) {
      const score: [number, number, number] = [Math.min(minGap, minSep), authored, minGap]
      const better = score[0] > bestScore[0] || (score[0] === bestScore[0] && (score[1] > bestScore[1] || (score[1] === bestScore[1] && score[2] > bestScore[2])))
      if (better) { bestScore = score; best = [...pick] }
      return
    }
    const n = slots[i]!.feet.length
    if (!n) { pick[i] = 0; walk(i + 1, minGap, authored); return }
    for (let a = 0; a < n; a++) {
      let g = Math.min(minGap, toFixed[i]![a]!)
      for (let j = 0; j < i; j++) if (slots[j]!.feet.length) g = Math.min(g, gapOf(j, pick[j]!, i, a))
      pick[i] = a
      // An earlier example's authored spot outweighs every later one's.
      walk(i + 1, g, authored + (a === 0 ? 2 ** (slots.length - i) : 0))
    }
  }
  walk(0, Infinity, 0)
  return best
}

/** Put the chosen item first, keeping the rest in order. */
const chosenFirst = <T,>(items: readonly T[], chosen: T | undefined) => (chosen === undefined ? [...items] : [chosen, ...items.filter((x) => x !== chosen)])

/** One placed overview example, for tests: its fact and the board points it covers. */
export interface PlacedExample { row: string; fact: number; points: Pt[] }

const WARP_BULGE = { near: 0.22, far: -0.32 }
const REACH_BULGE = 0.12
const MATCHUP_EXAMPLE_BULGE = 0.2

/**
 * Where each rule's overview example goes. Every fact with an example (a
 * reach fan, a matchup arrow and its dice tag, a link arrow) takes its own
 * spot: warp links are all drawn, so they are placed first; then, in rule
 * order, each example avoids the ones already placed (`pickExample`). Without
 * this a tower's reach fan and the "plains attack towers 5 v 7" arrow landed
 * on the same tower and read as one rule.
 */
export function placeRuleExamples(map: MapDef): { placed: PlacedExample[]; reach: Map<string, string[]>; pairs: Map<string, Array<[string, string]>> } {
  const overlay = map.ruleOverlay
  const reach = new Map<string, string[]>()
  const pairs = new Map<string, Array<[string, string]>>()
  const placed: PlacedExample[] = []
  if (!overlay) return { placed, reach, pairs }
  const sets = resolveRuleSets(map, overlay)
  const pos = (id: string) => centreOf(map, id)
  const span = Math.max(map.viewBox.width, map.viewBox.height)
  const minSep = exampleSeparation(map)
  const occupied: Pt[] = []
  const arrowFoot = (a: string, b: string, bulge: number, tag: boolean): Pt[] => {
    const pa = pos(a)
    const pb = pos(b)
    if (!pa || !pb) return []
    return tag ? [...arrowPoints(pa, pb, bulge), arrowMid(pa, pb, bulge)] : arrowPoints(pa, pb, bulge)
  }
  const warpBulge = (a: string, b: string) => {
    const pa = pos(a)
    const pb = pos(b)
    return pa && pb && Math.hypot(pb.x - pa.x, pb.y - pa.y) > span * 0.45 ? WARP_BULGE.far : WARP_BULGE.near
  }
  const key = (row: string, fact: number) => `${row}:${fact}`
  const place = (row: string, fact: number, points: Pt[]) => {
    placed.push({ row, fact, points })
    occupied.push(...points)
  }

  // Fixed first: warp links are all drawn, each with its tag.
  overlay.rules.forEach((rule) => rule.facts.forEach((f, fi) => {
    if (f.kind !== 'link' || f.style !== 'warp') return
    const all = ruleLinkPairs(map, f, sets)
    pairs.set(key(rule.id, fi), all)
    place(rule.id, fi, all.flatMap(([a, b]) => arrowFoot(a, b, warpBulge(a, b), !!f.dice)))
  }))

  // Then one spot per example, apart from each other (solveExamples).
  const slots: Array<ExampleSlot & { apply: (choice: number) => void }> = []
  for (const rule of overlay.rules) {
    rule.facts.forEach((f, fi) => {
      const k = key(rule.id, fi)
      if (f.kind === 'reach') {
        const members = sets[rule.set]?.memberIds ?? []
        const toSet = f.to ? sets[f.to] : undefined
        const foot = (src: string) => {
          const ps = pos(src)
          return ps ? [ps, ...ruleReachTargets(map, src, toSet).flatMap((t) => arrowFoot(src, t, REACH_BULGE, false))] : []
        }
        const candidates = f.example ? chosenFirst(members, f.example) : members
        slots.push({ row: rule.id, fact: fi, feet: candidates.map(foot), apply: (c) => {
          const chosen = candidates[c]
          if (chosen !== undefined) reach.set(k, f.drawAll ? chosenFirst(members, chosen) : [chosen])
        } })
      }
      if (f.kind === 'matchup') {
        const fromSet = sets[f.from]
        const toSet = sets[f.to]
        const all = fromSet && toSet ? ruleMatchupPairs(map, fromSet, toSet) : []
        if (f.drawAll) {
          // The tag sits on the defending set's first member, whichever arrow leads.
          const at = toSet ? pos(toSet.memberIds[0] ?? '') : null
          slots.push({ row: rule.id, fact: fi, feet: all.map(([a, b]) => [...arrowFoot(a, b, 0, false), ...(at ? [at] : [])]), apply: (c) => { pairs.set(k, chosenFirst(all, all[c])) } })
        } else if (f.example) {
          const authored = f.example
          const candidates: Array<[string, string]> = [authored, ...all.filter(([a, b]) => a !== authored[0] || b !== authored[1])]
          // Its tag sits on the attacking end (buildMapRulesModel).
          slots.push({ row: rule.id, fact: fi, feet: candidates.map(([a, b]) => [...arrowFoot(a, b, MATCHUP_EXAMPLE_BULGE, false), ...(pos(a) ? [pos(a)!] : [])]), apply: (c) => { pairs.set(k, [candidates[c] ?? authored]) } })
        }
      }
      if (f.kind === 'link' && f.style !== 'warp') {
        const all = ruleLinkPairs(map, f, sets)
        slots.push({ row: rule.id, fact: fi, feet: all.map(([a, b]) => arrowFoot(a, b, 0.08, !!f.dice)), apply: (c) => { pairs.set(k, chosenFirst(all, all[c])) } })
      }
    })
  }
  const choice = solveExamples(slots, occupied, minSep)
  slots.forEach((slot, i) => {
    slot.apply(choice[i]!)
    const points = slot.feet[choice[i]!]
    if (points) place(slot.row, slot.fact, points)
  })
  return { placed, reach, pairs }
}

// The placement search runs once per map object (the screen rebuilds the model on every render).
const examplesByMap = new WeakMap<MapDef, ReturnType<typeof placeRuleExamples>>()
function cachedExamples(map: MapDef) {
  let found = examplesByMap.get(map)
  if (!found) { found = placeRuleExamples(map); examplesByMap.set(map, found) }
  return found
}

/**
 * The ★ overlay for a map with a `ruleOverlay` (design `overlay()` in
 * tail1.js). `compact` = phone sizes.
 */
export function buildMapRulesModel(map: MapDef, palette: OverlayPalette, { compact = false, calm = true }: { compact?: boolean; calm?: boolean } = {}): OverlayModel | null {
  const overlay = map.ruleOverlay
  if (!overlay) return null
  const sets = resolveRuleSets(map, overlay)
  const setById = new Map(overlay.sets.map((s) => [s.id, s]))
  const setLabel: SetLabel = (id) => (id ? setById.get(id)?.label ?? id : '')
  const examples = cachedExamples(map)
  const marks: OverlayMark[] = []
  const sw = compact ? 1.8 : 2.6
  const pos = (id: string) => centreOf(map, id)
  const span = Math.max(map.viewBox.width, map.viewBox.height)
  const dashedTarget = (id: string, color: string, row: string, detail: boolean, fill: boolean) => {
    marks.push({ kind: 'lit', row, detail, territoryIds: [id], regionIds: [], color, fillOpacity: fill ? 0.12 : 0, strokePx: sw * 0.8, dashed: true })
  }
  const arrow = (row: string, detail: boolean, from: Pt, to: Pt, a: Omit<Extract<OverlayMark, { kind: 'arrow' }>, 'kind' | 'row' | 'detail' | 'from' | 'to'>) => {
    marks.push({ kind: 'arrow', row, detail, from, to, ...a })
    return marks.length - 1
  }
  const diceTag = (row: string, detail: boolean, at: Pt | { arrowOf: number }, attack: number, defend: number, label: string, dyPx = 0, roles?: Extract<OverlayMark, { kind: 'tag' }>['roles'], dxPx = 0) => {
    marks.push({ kind: 'tag', row, detail, at, dyPx, dxPx, text: `${attack} v ${defend}`, dice: { attack, defend }, roles, label, color: diceColor(diceEdge(attack, defend), palette) })
  }

  for (const rule of overlay.rules) {
    const set = setById.get(rule.set)
    if (!set) continue
    const ruleSet = rule.label ?? set.label
    // The row's sets: its own, then `also` (detail only in the calm overview).
    ;[rule.set, ...(rule.also ?? [])].forEach((gid, gi) => {
      const g = sets[gid]
      const gs = setById.get(gid)
      if (!g || !gs) return
      const detail = calm && gi > 0
      if (g.territoryIds.length) marks.push({ kind: 'lit', row: rule.id, detail, territoryIds: g.territoryIds, regionIds: [], color: gs.color, fillOpacity: 0.16, strokePx: sw, glow: true })
      if (g.regionIds.length) marks.push({ kind: 'lit', row: rule.id, detail, territoryIds: [], regionIds: g.regionIds, color: gs.color, fillOpacity: 0.1, strokePx: sw + 0.6, glow: true })
    })
    rule.facts.forEach((f, fi) => {
      const exKey = `${rule.id}:${fi}`
      if (f.kind === 'link') {
        // The overview example leads (placeRuleExamples); warp links are all drawn.
        const linkPairs = examples.pairs.get(exKey) ?? ruleLinkPairs(map, f, sets)
        const color = f.style === 'sea' ? setById.get(f.from)?.color ?? set.color
          : f.style === 'oneway' ? setById.get(f.to ?? '')?.color ?? set.color : set.color
        const words = f.dice ? factSentence(f, setLabel, ruleSet) : ''
        const seen = new Set<string>()
        linkPairs.forEach(([a, b], i) => {
          const pa = pos(a)
          const pb = pos(b)
          if (!pa || !pb) return
          const detail = calm && f.style !== 'warp' && i > 0
          const far = Math.hypot(pb.x - pa.x, pb.y - pa.y) > span * 0.45
          const idx = arrow(rule.id, detail, pa, pb, {
            color, widthPx: compact ? 2 : 3, bulge: f.style === 'warp' ? (far ? WARP_BULGE.far : WARP_BULGE.near) : 0.08,
            head: f.style === 'oneway', startGapPx: compact ? 5 : 9, endGapPx: compact ? 6 : 10,
            dash: f.style === 'warp' || f.style === 'sea' ? '7 6' : undefined,
          })
          if (f.dice) {
            if (f.style === 'warp' || i === 0) diceTag(rule.id, detail, { arrowOf: idx }, f.dice.attack, f.dice.defend, words)
            else if (i === Math.floor(linkPairs.length / 2)) diceTag(rule.id, calm, { arrowOf: idx }, f.dice.attack, f.dice.defend, words)
          }
          if ((f.style === 'oneway' || f.style === 'sea') && !seen.has(b)) {
            seen.add(b)
            dashedTarget(b, color, rule.id, calm && i > 0, false)
          }
        })
      }
      if (f.kind === 'reach') {
        const sources = examples.reach.get(exKey) ?? []
        const targetDetail = new Map<string, boolean>()
        sources.forEach((s, si) => {
          const ps = pos(s)
          if (!ps) return
          const detail = calm && si > 0
          for (const t of ruleReachTargets(map, s, f.to ? sets[f.to] : undefined)) {
            const pt = pos(t)
            if (!pt) continue
            if (!targetDetail.has(t) || !detail) targetDetail.set(t, detail)
            arrow(rule.id, detail, ps, pt, { color: set.color, widthPx: compact ? 1.5 : 2.2, bulge: REACH_BULGE, head: true, startGapPx: compact ? 5 : 8, endGapPx: compact ? 4 : 7 })
          }
          if (!f.drawAll) {
            marks.push({ kind: 'ring', row: rule.id, detail, at: ps, color: set.color, radiusPx: compact ? 9 : 15 })
            // The legend's reach chip, on the map beside the example, right of
            // the tower and clear of everything lit (maintainer, 2026-11-03).
            const targets = ruleReachTargets(map, s, f.to ? sets[f.to] : undefined).map(pos).filter((q): q is Pt => !!q)
            if (targets.length) {
              const text = `reach ${f.steps === 'area' ? 'area' : f.steps}${f.oneWay ? ' · one-way' : ''}`
              const label = factSentence({ ...f, attack: undefined }, setLabel, ruleSet)
              if (f.tagAt && s === f.example) {
                // On its authored spot, a dashed leader back to the tower so it
                // can only be read as this tower's (maintainer, 2026-11-03).
                arrow(rule.id, detail, ps, f.tagAt, { color: set.color, widthPx: compact ? 1.4 : 2, bulge: 0, head: false, startGapPx: compact ? 10 : 14, endGapPx: 0, dash: '4 4' })
                marks.push({ kind: 'tag', row: rule.id, detail, at: f.tagAt, dyPx: 0, text, color: set.color, glyph: { glyph: 'reach', color: set.color }, label })
              } else {
                marks.push({ kind: 'tag', row: rule.id, detail, at: ps, dyPx: 0, avoidLit: true, text, color: set.color, glyph: { glyph: 'reach', color: set.color }, label })
              }
            }
          }
        })
        for (const [t, detail] of targetDetail) dashedTarget(t, set.color, rule.id, detail, true)
      }
      if (f.kind === 'matchup') {
        const fromSet = sets[f.from]
        const toSet = sets[f.to]
        const color = setById.get(f.from)?.color ?? set.color
        const words = factSentence(f, setLabel, ruleSet)
        const matchPairs = examples.pairs.get(exKey) ?? []
        matchPairs.forEach(([a, b], i) => {
          const pa = pos(a)
          const pb = pos(b)
          if (!pa || !pb) return
          const detail = calm && !!f.drawAll && i > 0
          // The single example runs count to count on an arc, so it shows even
          // when the two counts sit close together (maintainer, 2026-11-03).
          arrow(rule.id, detail, pa, pb, {
            // The example's arrow takes its dice tag's colour, so the two read as one.
            color: f.drawAll ? color : diceColor(diceEdge(f.attack, f.defend), palette), widthPx: compact ? 2.8 : 3.8, bulge: f.drawAll ? 0 : -0.18,
            head: true, startGapPx: f.drawAll ? (compact ? 3 : 5) : (compact ? 7 : 9), endGapPx: f.drawAll ? (compact ? 6 : 10) : (compact ? 8 : 10),
          })
          // The tag sits on the attacking space, the arrow running from it into
          // the defender, each number led by its side's glyph (maintainer,
          // 2026-11-03: plains attack towers 5 v 7 read as one rule).
          if (!f.drawAll) {
            const from = setById.get(f.from)
            const to = setById.get(f.to)
            const roles = from && to ? { attack: { glyph: from.glyph, color: from.color }, defend: { glyph: to.glyph, color: to.color } } : undefined
            // To the right of the attacker's count, clear of it and of the
            // arrow (maintainer, 2026-11-03).
            const rightward = pb.x > pa.x
            diceTag(rule.id, false, pa, f.attack, f.defend, words, rightward ? (compact ? -12 : -15) : 0, roles, compact ? 52 : 62)
          }
          marks.push({ kind: 'hole', row: rule.id, detail, territoryIds: [a] })
        })
        if (f.drawAll && matchPairs.length && fromSet && toSet) {
          const at = pos(toSet.memberIds[0] ?? matchPairs[0]![1])
          if (at) diceTag(rule.id, false, at, f.attack, f.defend, words, compact ? 16 : 26)
          for (const id of fromSet.memberIds) {
            marks.push({ kind: 'lit', row: rule.id, detail: calm && id !== matchPairs[0]![0], territoryIds: [id], regionIds: [], color, fillOpacity: 0, strokePx: sw * 0.8, hole: false })
          }
        }
      }
    })
  }

  // Region outlines (mesas) never paint into the territories other rules
  // light on top of them: the towers on their walls, the crystals inside.
  const territoryLit = [...new Set(marks.flatMap((m) => (m.kind === 'lit' && !m.regionIds.length && !m.dashed ? m.territoryIds : [])))]
  for (const m of marks) if (m.kind === 'lit' && m.regionIds.length) m.clipOut = territoryLit

  const rows: OverlayRow[] = overlay.rules.flatMap((rule) => {
    const set = setById.get(rule.set)
    if (!set) return []
    const label = rule.label ?? set.label
    return [{
      id: rule.id, label, color: set.color, tile: { kind: 'glyph' as const, glyph: set.glyph, color: set.color },
      chips: ruleChips(rule, overlay, palette),
      accessibilityLabel: `${label}: ${rule.facts.map((f) => factSentence(f, setLabel, label)).join(' ')}`,
    }]
  })
  const footer: FooterChip[] = (overlay.chips ?? []).map((c) => ({ glyph: c.icon, label: c.label, color: palette.amber }))
  return {
    kind: 'map',
    title: 'Special rules',
    rows,
    footer,
    diceKey: overlay.rules.some((r) => r.facts.some(hasDice)),
    marks,
    accessibilitySummary: [`Special rules for ${map.name}.`, ...(map.specialRules ?? rows.map((r) => r.accessibilityLabel))].join(' '),
  }
}

// ─── Team play ──────────────────────────────────────────────────────────────

export interface TeamOverlayInput {
  map: MapDef
  teams?: TeamMembership
  /** Turn order. */
  players: readonly PlayerId[]
  viewerId: PlayerId
  /** Player colour, as the board fills their land. */
  colorOf: (playerId: PlayerId) => string
  nameOf: (playerId: PlayerId) => string
}

/**
 * The examples the team overlay points at, picked from the live board
 * (design `teamsFor`): the you→teammate border nearest the centre (fortify),
 * a teammate's front-line territory (place), and a teammate→enemy border (the
 * attack you cannot make from their land).
 */
export function teamExamples({ map, teams, players, viewerId }: Pick<TeamOverlayInput, 'map' | 'teams' | 'players' | 'viewerId'>) {
  const state = { teams, players }
  const team = teamOf(state, viewerId)
  const owners = new Map(map.territories.map((t) => [t.id, t.ownerId]))
  const isMate = (owner: string | undefined | null) => !!owner && owner !== viewerId && isAlly(state, viewerId, owner)
  const isEnemy = (owner: string | undefined | null) => !!owner && owner !== viewerId && !isAlly(state, viewerId, owner) && teamOf(state, owner) !== null
  const cx = map.viewBox.x ?? 0
  const cy = map.viewBox.y ?? 0
  const centre = { x: cx + map.viewBox.width / 2, y: cy + map.viewBox.height / 2 }
  const dc = (t: MapDef['territories'][number]) => Math.hypot(t.position.x - centre.x, t.position.y - centre.y)
  const byId = new Map(map.territories.map((t) => [t.id, t]))
  const pairs = (from: (o: string | undefined) => boolean, to: (o: string | undefined) => boolean): Array<[string, string]> => {
    const out: Array<[string, string, number]> = []
    for (const t of map.territories) {
      if (!from(t.ownerId)) continue
      for (const n of [...t.adjacencies, ...(t.oneWayBorders ?? [])]) {
        const u = byId.get(n)
        if (u && to(owners.get(n)) && hasBorderTo(t, n)) out.push([t.id, n, dc(t) + dc(u)])
      }
    }
    return out.sort((a, b) => a[2] - b[2]).map(([a, b]) => [a, b])
  }
  const fortify = pairs((o) => o === viewerId, isMate)[0] ?? null
  const mateFront = pairs(isMate, isEnemy)
  const place = (mateFront.find((p) => !fortify || p[0] !== fortify[1]) ?? mateFront[0])?.[0]
    ?? map.territories.find((t) => isMate(t.ownerId))?.id ?? null
  const noAttack = mateFront.find((p) => p[0] !== place && (!fortify || p[0] !== fortify[1])) ?? mateFront.find((p) => p[0] !== place) ?? mateFront[0] ?? null
  return { team, fortify, place, noAttack }
}

/** The team play overlay for the viewer, or null outside a team game. */
export function buildTeamRulesModel(input: TeamOverlayInput, palette: OverlayPalette): OverlayModel | null {
  const { map, teams, players, viewerId, colorOf, nameOf } = input
  const state = { teams, players }
  const team = teamOf(state, viewerId)
  if (!team) return null
  const mates = teamMembers(state, team).filter((p) => p !== viewerId)
  const { place } = teamExamples(input)
  const mainMate = (place && map.territories.find((t) => t.id === place)?.ownerId) || mates[0]
  const mateColor = mainMate ? colorOf(mainMate) : palette.olive

  // One short sentence per rule, laid out as a full-width line so it reads at
  // any legend width (maintainer, 2026-11-03).
  const chip = (color: string, icon: string, text: string): ChipSpec => ({
    color, line: true, parts: [{ t: 'icon' as const, name: icon, color }, { t: 'label', text }],
  })
  const mateName = mainMate ? nameOf(mainMate) : 'Teammate'
  const rows: OverlayRow[] = [
    {
      id: 'tm-mate', label: 'Teammates', color: mateColor,
      tile: { kind: 'team', team, color: mateColor, initial: mateName.slice(0, 1).toUpperCase() },
      chips: [chip(palette.good, 'plus', 'May place troops on teammate territories'), chip(palette.good, 'arrow-right-bold', 'May fortify to teammate territories')],
      accessibilityLabel: "Teammate: you can place troops on a teammate's territory, and fortify one step from your own territory into a teammate's.",
    },
    {
      id: 'tm-attack', label: 'Attack', color: palette.bad, tile: { kind: 'icon', name: 'sword', color: palette.bad },
      chips: [chip(palette.bad, 'close', 'Only from your own land'), chip(palette.amber, 'alert-outline', 'You can attack a teammate (it will warn you)')],
      accessibilityLabel: "Attack: only from your own territory, never from a teammate's. Attacking a teammate is allowed, with a warning first.",
    },
    {
      id: 'tm-win', label: 'Win', color: palette.brass, tile: { kind: 'icon', name: 'trophy-outline', color: palette.brass },
      chips: [chip(palette.brass, 'trophy-outline', 'Last team standing wins'), chip(palette.brass, 'account-group', 'Knocked-out teammates win too'), chip(palette.bad, 'flag-outline', 'Resigning counts as a loss')],
      accessibilityLabel: 'Win: the last team standing wins. Every member wins, even one knocked out in battle. Resigning counts as a loss.',
    },
  ]
  const footer: FooterChip[] = [
    { icon: 'eye-outline', label: 'See teammates’ cards', color: palette.good },
    { icon: 'forum-outline', label: 'Private full team chat', color: palette.good },
    { icon: 'close', label: 'Shared team region: no bonus', color: palette.bad },
  ]
  return {
    kind: 'team',
    // Your team, and who is on it (maintainer, 2026-11-03).
    title: teamLabel(team),
    subtitle: [viewerId, ...mates].map(nameOf).join(' + '),
    rows,
    footer,
    // Shown in a modal from the game menu, never over the map (maintainer, 2026-11-03).
    marks: [],
    accessibilitySummary: [
      `Team play. ${teamLabel(team)}: you${mates.length ? ` and ${mates.map(nameOf).join(' and ')}` : ''}.`,
      ...rows.map((r) => r.accessibilityLabel),
      'You can see your teammates’ cards and use a team chat. A region shared between teammates pays nobody.',
    ].join(' '),
  }
}

/**
 * Which rules and filter entry points a game offers: the map bar's ★ when the
 * map has a rules overlay, and its Players filter in every game, both only in
 * a live game (not the snapshot view); the game menu's "Team play rules"
 * when the viewer is on a team.
 */
export function rulesEntryPoints({ map, teams, viewerId, live }: { map: Pick<MapDef, 'ruleOverlay'>; teams?: TeamMembership; viewerId: PlayerId | null | undefined; live: boolean }) {
  return {
    rules: live && !!map.ruleOverlay,
    players: live,
    team: live && !!viewerId && teamOf({ teams }, viewerId) !== null,
  }
}

/** The game menu's "Team play rules" modal: your team, its members and the rules. */
export interface TeamRulesInfo {
  team: TeamId
  /** Your team in turn order, you included. */
  members: Array<{ id: PlayerId; name: string; color: string; you: boolean }>
  /** The rows and footer chips (no board marks). */
  model: OverlayModel
}

export function buildTeamRulesInfo(input: TeamOverlayInput, palette: OverlayPalette): TeamRulesInfo | null {
  const model = buildTeamRulesModel(input, palette)
  const team = teamOf({ teams: input.teams, players: input.players }, input.viewerId)
  if (!model || !team) return null
  const members = teamMembers({ teams: input.teams, players: input.players }, team)
    .map((id) => ({ id, name: input.nameOf(id), color: input.colorOf(id), you: id === input.viewerId }))
  return { team, members, model }
}
